import { FormEvent, useState } from "react"
import { useModalWindow } from "react-modal-global"
import ZodForm, { useZodFormIssues } from "react-zod-form"
import { coerce, z } from "zod"

import { companyAvatarColors } from "@/constants/companyAvatarColor"
import { CompanyUpdateDTO } from "@/entities/company/company.dto"
import { CompanyBase } from "@/entities/company/company.types"
import PopupLayout from "@/services/Modal/layouts/PopupLayout/PopupLayout"
import EditableAvatar from "@/shared/components/extrinsic/EditableAvatar/EditableAvatar"
import Button from "@/shared/components/intrinsic/Button/Button"
import Field from "@/shared/components/intrinsic/Field/Field"
import Fields from "@/shared/layouts/Fields/Fields"
import useFormStatus from "@/utils/hooks/useFormStatus"
import Bytes from "@/utils/transform/Bytes"

const form = new ZodForm({
  companyName: coerce.string().min(1, "Must contain at least 1 character"),
  companyLogo: z.instanceof(File).optional()
})
form.object._type satisfies CompanyUpdateDTO

interface PopupEditCompanyProps {
  company: CompanyBase
  onSubmit?(fields: CompanyUpdateDTO): Promise<void> | void
}

const PopupEditCompany = ({ company, onSubmit }: PopupEditCompanyProps) => {
  const modal = useModalWindow()
  const { fieldIssues } = useZodFormIssues(form)
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [companyName, setCompanyName] = useState(company.title || null)

  const formStatus = useFormStatus(form, handleSubmit)

  const [isChanged, setIsChanged] = useState(false)
  const [initialCompanyData] = useState({
    logo: company.logo,
    title: company.title
  })
  
  const companyFirstLetter = companyName?.charAt(0)?.toUpperCase() || ""
  const showAvatarCover = !company.logo && !avatarFile

  function onChanges(event: FormEvent<HTMLFormElement>) {
    const fields = form.parseAllFields(event)
    if(fields == null) return

    const isCompanyNameChanged = fields.companyName ? fields.companyName !== initialCompanyData.title : false
    const isCompanyLogoChanged = fields.companyLogo ? fields.companyLogo.name !== initialCompanyData.logo : false

    setIsChanged(isCompanyNameChanged || isCompanyLogoChanged)
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    const fields = form.parseAllFields(event)
    if(fields == null) return

    // Include avatar file in the fields if it exists
    const dto: CompanyUpdateDTO = {
      ...fields,
      companyLogo: avatarFile || fields.companyLogo
    }

    await onSubmit?.(dto)
    setIsChanged(false)
    modal.close()
  }

  function handleNameChange(event: FormEvent<HTMLInputElement>) {
    setCompanyName(event.currentTarget.value)
  }

  function handleAvatarChange(file: File) {
    setAvatarFile(file)
    setIsChanged(true)
  }

  return (
    <PopupLayout title="Updating the company" width="50em">
      <form onChange={onChanges} onSubmit={formStatus.submit}>
        <Fields>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <EditableAvatar 
              onChange={handleAvatarChange} 
              contain 
              name={form.fields.companyLogo} 
              maxSize={Bytes.MB(10)} 
              showCover={showAvatarCover} 
              firstName={companyFirstLetter} 
              defaultImage={company.logo}
              {...companyAvatarColors}
            />
          </div>
          <Field onChange={handleNameChange} name={form.fields.companyName} placeholder="Company Name" defaultValue={company.title} customValidity={fieldIssues.companyName} />
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Button type="submit" color="white" {...formStatus} disabled={!isChanged}>Update</Button>
          </div>
        </Fields>
      </form>
    </PopupLayout>
  )
}

export default PopupEditCompany
